import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth-context.js';
import { appendNext, sanitizeReturnTo } from '../return-to.js';

export interface AuthGuardProps {
  children: ReactNode;
  /** If set, require this role; otherwise any authenticated user passes. */
  requireRole?: string;
  redirectTo?: string;
  /** Custom UI when authenticated but missing `requireRole`. */
  deniedFallback?: ReactNode;
}

/** Gate a route on authentication (and optionally a role). Presentation-only decisions. */
export function AuthGuard({
  children,
  requireRole,
  redirectTo = '/login',
  deniedFallback,
}: AuthGuardProps) {
  const { status, hasRole, logout, user } = useAuth();
  const location = useLocation();

  if (status === 'initializing' || status === 'authenticating') {
    return <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif' }}>Loading…</div>;
  }
  if (status !== 'authenticated') {
    const next = sanitizeReturnTo(`${location.pathname}${location.search}`);
    return <Navigate to={appendNext(redirectTo, next)} replace />;
  }
  if (requireRole && !hasRole(requireRole)) {
    return (
      deniedFallback ?? (
        <div
          style={{
            display: 'grid',
            placeItems: 'center',
            minHeight: '100vh',
            padding: '2rem',
            fontFamily: 'system-ui, sans-serif',
            textAlign: 'center',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 8 }}>
              Access denied — {requireRole} role required
            </div>
            <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: 16 }}>
              Signed in as {user?.email ?? 'this account'}. Use an admin account, or sign out and
              open the public app instead.
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              style={{
                padding: '10px 16px',
                borderRadius: 8,
                border: '1px solid #ccc',
                background: '#111',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      )
    );
  }
  return <>{children}</>;
}
